import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Formik, Form, Field, ErrorMessage as FormikErrorMessage } from 'formik';
import * as Yup from 'yup';
// import { AiFillHome } from 'react-icons/ai'; // 不要なのでコメントアウト
import './Login.css';

// 認証コンテキスト
import { useAuth } from '../contexts/AuthContext';

// ログインエラー状態をグローバルに管理（コンポーネント再マウントに影響されない）
let globalLoginError = '';
let globalLoginWarning = '';

const setGlobalLoginError = (error) => {
  globalLoginError = error;
};

const setGlobalLoginWarning = (warning) => {
  globalLoginWarning = warning;
};

const clearGlobalLoginMessages = () => {
  globalLoginError = '';
  globalLoginWarning = '';
};

function Login() {
  const navigate = useNavigate();
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [sessionExpiredMessage, setSessionExpiredMessage] = useState('');
  const { login } = useAuth();
  
  // グローバル状態をReact状態にマッピング（表示用）
  const [displayError, setDisplayError] = useState(globalLoginError);
  const [displayWarning, setDisplayWarning] = useState(globalLoginWarning);
  
  // グローバル状態の変更を定期的にチェック
  useEffect(() => {
    const checkGlobalState = () => {
      if (displayError !== globalLoginError) {
        setDisplayError(globalLoginError);
      }
      if (displayWarning !== globalLoginWarning) {
        setDisplayWarning(globalLoginWarning);
      }
    };
    
    // 初回チェック
    checkGlobalState();
    
    // 定期チェック（100ms間隔）
    const interval = setInterval(checkGlobalState, 100);
    
    return () => clearInterval(interval);
  }, [displayError, displayWarning]);



  // 登録完了（メール認証が必要）メッセージのチェック
  useEffect(() => {
    const postRegister = sessionStorage.getItem('post_register_message');
    if (postRegister) {
      setSessionExpiredMessage(postRegister);
      sessionStorage.removeItem('post_register_message');
    }
  }, []);

  // セッション期限切れメッセージをチェック
  useEffect(() => {
    const expiredReason = sessionStorage.getItem('session_expired_reason');
    const redirectPath = sessionStorage.getItem('redirect_after_login');
    
    if (expiredReason) {
      switch (expiredReason) {
        case 'token_expired':
          setSessionExpiredMessage('セッションの有効期限が切れました。再度ログインしてください。');
          break;
        case 'api_401_error':
          setSessionExpiredMessage('認証の有効期限が切れました。再度ログインしてください。');
          break;
        case 'periodic_check':
          setSessionExpiredMessage('セッションタイムアウトが発生しました。再度ログインしてください。');
          break;
        default:
          setSessionExpiredMessage('セッションが切断されました。再度ログインしてください。');
      }
      
      // メッセージを表示したら期限切れ理由をクリア
      sessionStorage.removeItem('session_expired_reason');
    }
    
    // リダイレクト先の情報表示（オプション）
    if (redirectPath && !expiredReason) {
      setSessionExpiredMessage('ログイン後、元のページに戻ります。');
    }
  }, []);

  // バリデーションスキーマ
  const validationSchema = Yup.object({
    login: Yup.string()
      .required('ユーザー名またはメールアドレスは必須です'),
    password: Yup.string()
      .required('パスワードは必須です')
  });

  // ログイン処理
  const handleSubmit = async (values, { setSubmitting }) => {
    // グローバルエラーメッセージをクリア
    clearGlobalLoginMessages();
    setDisplayError('');
    setDisplayWarning('');
    
    setIsLoggingIn(true);
    
    try {
      // AuthContextのログイン関数を呼び出す
      const result = await login({
        login: values.login,
        password: values.password
      });
      
      if (result && result.user) {
        // ログイン成功後は少し待ってからリダイレクト（認証状態の更新を待つ）
        setTimeout(() => {
          // リダイレクト先をsessionStorageから取得
          const redirectPath = sessionStorage.getItem('redirect_after_login');
          
          // リダイレクト先があればそこに遷移、なければホームページに戻る
          if (redirectPath) {
            sessionStorage.removeItem('redirect_after_login');
            navigate(redirectPath, { replace: true });
          } else {
            navigate('/', { replace: true });
          }
        }, 100);
      }
    } catch (error) {
      
      let errorMessage = 'ログイン処理中にエラーが発生しました';
      let warningMessage = '';
      
      // AuthContextからのエラーオブジェクトを処理
      if (error.status) {
        // AuthContextから返されたエラー（statusプロパティ付き）
        errorMessage = error.message;
        if (error.warning) {
          warningMessage = error.warning;
        }
        
        // ステータスコード別の追加処理
        if (error.status === 403) {
          // 403 Forbidden - IPロックまたはアカウントロック
          errorMessage = error.message || 'アカウントまたはIPアドレスがロックされています';
        } else if (error.status === 423) {
          // 423 Locked - 明示的なロック状態
          errorMessage = error.message || 'ログイン試行回数が上限に達しました';
        }
      } else if (error.response) {
        // Axiosエラー（従来の処理）
        const responseData = error.response.data || {};
        
        if (error.response.status === 401) {
          errorMessage = responseData.error || 'ユーザー名/メールアドレスまたはパスワードが正しくありません';
          if (responseData.warning) {
            warningMessage = responseData.warning;
          }
        } else if (error.response.status === 423) {
          errorMessage = responseData.error || 'アカウントまたはIPアドレスがロックされています';
        } else if (error.response.status === 403) {
          // FastAPI は detail で返すため error/detail 両方をフォールバック
          errorMessage = responseData.error || responseData.detail || 'アクセスが許可されていません';
        } else if (error.response.status === 400) {
          errorMessage = responseData.error || responseData.detail || 'リクエストが無効です';
        } else if (error.response.status >= 500) {
          errorMessage = 'サーバーでエラーが発生しました。しばらくしてから再試行してください';
        }
      } else if (error.request) {
        errorMessage = 'ネットワークエラーが発生しました。インターネット接続を確認してください';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      // グローバル状態にエラーメッセージを設定（コンポーネント再マウントに影響されない）
      setGlobalLoginError(errorMessage);
      setGlobalLoginWarning(warningMessage);
      
      // 表示用状態も更新
      setDisplayError(errorMessage);
      setDisplayWarning(warningMessage);
      setIsLoggingIn(false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="login">
      <div className="login__container">
        <div className="login__logo">
          <img src="/images/DNS.svg" alt="DNS Logo" />
        </div>
        <h2>ログイン</h2>
        
        {sessionExpiredMessage && (
          <div className="login__info-message">
            {sessionExpiredMessage}
          </div>
        )}
        
        {/* グローバル状態によるエラー表示 */}
        {displayError && (
          <div className="login__error">
            {displayError}
          </div>
        )}
        
        {displayWarning && (
          <div className="login__warning">
            {displayWarning}
          </div>
        )}
        

        
        <Formik
          initialValues={{ login: '', password: '' }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting }) => (
            <Form className="login__form">
              <div className="form-group">
                <label htmlFor="login">ユーザー名またはメールアドレス</label>
                <Field
                  type="text"
                  name="login"
                  id="login"
                  placeholder="ユーザー名またはメールアドレスを入力"
                />
                <FormikErrorMessage name="login" component="div" className="error-message" />
              </div>
              
              <div className="form-group">
                <label htmlFor="password">パスワード</label>
                <Field
                  type="password"
                  name="password"
                  id="password"
                  placeholder="パスワードを入力"
                />
                <FormikErrorMessage name="password" component="div" className="error-message" />
              </div>
              
              <button
                type="submit"
                className="login__button"
                disabled={isSubmitting || isLoggingIn}
              >
                {isLoggingIn ? 'ログイン中...' : 'ログイン'}
              </button>
            </Form>
          )}
        </Formik>
        
        <div className="login__footer">
          <p>
            アカウントをお持ちでないですか？{' '}
            <Link to="/register">登録する</Link>
            <span className="note">（閲覧ユーザーとして登録されます）</span>
          </p>
          <p className="login__reset-link">
            <Link to="/forgot-password">パスワードをお忘れですか？</Link>
          </p>
        </div>
      </div>
    </div>
  );
}

export default Login; 